import { z } from 'zod';

/**
 * Каталог деловых сфер — словарь интересов режима BUSINESS. Зеркало `interests.ts`:
 * та же форма (slug → русская подпись), другой смысл.
 *
 * Почему отдельный словарь, а не расширение личного: хобби деловое намерение не
 * описывают. «Йога и растяжка» о деловом человеке не говорит ничего, а «Недвижимость»
 * бессмысленна как увлечение — поэтому режимы валидируются каждый своим списком
 * (`interestSlugsForMode` в `networking.ts`).
 *
 * Список фиксированный (не админ-каталог), порядок — для пикера. Выбирается максимум
 * `MAX_INTERESTS` = 3, поэтому длина списка сама себе ограничение: длинный пикер
 * перестают дочитывать.
 */
export const BUSINESS_INTEREST_SLUGS = [
  // Технологии и цифра
  'it',
  'saas',
  'ai-ml',
  'crypto-web3',
  'design',
  // Продажи и рост
  'marketing',
  'sales',
  'ecommerce',
  'retail',
  'media-content',
  // Деньги и активы
  'finance',
  'investments',
  'real-estate',
  'construction',
  // Сервис и гостеприимство
  'hospitality',
  'food-beverage',
  'tourism',
  'beauty-wellness',
  // Люди и знания
  'education',
  'health-med',
  'hr-recruiting',
  'legal',
  'consulting',
  'logistics',
] as const;

export const BusinessInterestSlug = z.enum(BUSINESS_INTEREST_SLUGS);
export type BusinessInterestSlug = z.infer<typeof BusinessInterestSlug>;

/** Русские названия деловых сфер по slug. */
export const BUSINESS_INTEREST_LABELS: Record<BusinessInterestSlug, string> = {
  it: 'IT и разработка',
  saas: 'SaaS и IT-продукты',
  'ai-ml': 'ИИ и данные',
  'crypto-web3': 'Крипто и Web3',
  design: 'Дизайн и продукт',
  marketing: 'Маркетинг и реклама',
  sales: 'Продажи и развитие бизнеса',
  ecommerce: 'E-commerce и маркетплейсы',
  retail: 'Розница и торговля',
  'media-content': 'Медиа и контент',
  finance: 'Финансы и бухгалтерия',
  investments: 'Инвестиции и венчур',
  'real-estate': 'Недвижимость',
  construction: 'Стройка и ремонт',
  hospitality: 'Отели и гостеприимство',
  'food-beverage': 'Рестораны и кафе',
  tourism: 'Туризм и путешествия',
  'beauty-wellness': 'Красота и велнес',
  education: 'Образование и обучение',
  'health-med': 'Медицина и здоровье',
  'hr-recruiting': 'HR и подбор персонала',
  legal: 'Юридические услуги',
  consulting: 'Консалтинг и агентства',
  logistics: 'Логистика и перевозки',
};

/** Каталог в виде массива `{ slug, label }` в каноническом порядке — для пикеров. */
export const BUSINESS_INTEREST_CATALOG: ReadonlyArray<{ slug: BusinessInterestSlug; label: string }> =
  BUSINESS_INTEREST_SLUGS.map((slug) => ({ slug, label: BUSINESS_INTEREST_LABELS[slug] }));
